import ApiService from './api';
import moment from 'moment';
import type {
  DeleteFormResult,
  FormUsageReport,
} from '@/components/forms/formUsageAnalysis/types';

/** Options for {@link ProductService.streamDeploymentDetails}. */
interface StreamDeploymentOptions {
  /** Called with each parsed `details` payload as it streams in */
  onPart?: (part: any) => void;
  /** Called once with the total number of parts (`totalParts` message) */
  onSize?: (size: number) => void;
  /** Called when the stream finished */
  onFinish?: (...args: any[]) => void;
  /** Abort signal to cancel the stream */
  signal?: AbortSignal;
}

/** Result of a K2 classification check. */
export interface K2CheckResult {
  /** The intent that was detected */
  label: string;
  /** The confidence of the detected intent */
  confidence: number;
  /** The model ID used for the check */
  modelId: string;
  /**
   * The protocol details of the check.
   * Populated if you request enriched protocol details (`withProtocol: true`)
   */
  protocol?: K2Protocol | null;
}

/**
 * An indicator that the user's query (`text`) should NOT match the current found intent.
 * This is the flip-side of {@link K2ProtocolFeedbackPair};
 *
 * You find a negative feedback if the LLM detected the current intent as the solution for `text`,
 * but an expert corrected it to not be.
 *
 * "You might think user asks for <this intent>, but it is actually <another>".
 * What `<another>` is in this case is not made known.
 */
export interface K2ProtocolNegativeFeedback {
  /**
   * A text that the user asked.
   * This query should NOT find the currently detected intent.
   */
  text: string;
  /** The option picked, e.g. "2". Not really useful. */
  label: string;
}

/**
 * Partial information from an expert's correction of what the right intent is;
 * "You might think the user asks for A, but it is actually B".
 *
 * The correction is included as examples ({@link K2Example}), but you won't find what the original bad match was.
 * E.g., you will find the `text` property and `posLabel` matches an {@link K2Example}'s `text` and `label` property.
 */
export interface K2ProtocolFeedbackPair {
  /** The user's query */
  text: string;
  /**
   * A reference to the correct intent, e.g. "1".
   * You won't find the intent, but it is implicitly the detected intent.
   */
  posLabel: string;
  /**
   * A reference to the wrong intent, e.g. "0". Not really useful.
   * You won't find what the original bad match was, only that the question is NOT supposed to be detected as this intent.
   */
  negLabel: string;
}

/** A single prompt message sent to the LLM. */
export interface K2Prompt {
  /** The role in the conversation (e.g., system, user) */
  role: string | 'system' | 'user' | 'assistant';
  /** The content of the prompt */
  content: string;
}

/** Timing breakdown of a K2 check. */
export interface K2TimeTracking {
  /** The time taken for K2 intent indexing */
  k2IntentIndex: number;
  /** The time taken for K2 feedback indexing */
  k2FeedbackIndex: number;
  /** The time taken for the LLM call */
  llmCall: number;
}

/** An intent candidate presented to / indexed for the LLM. */
export interface K2Intent {
  /** The intent name */
  intent: string;
  /** The display name of the intent. In very rare cases can be omitted */
  displayName?: string | null;
  /** The description of the intent */
  description: string;
  /** The K2 names associated with the intent */
  k2names: string[];
  /** The score of the intent */
  score: number;
  /** Example text associated with the intent, when present */
  text?: string | null;
}

/** A reference example fed to the model. */
export interface K2Example {
  /** An example of a user query (can be AI generated or a real question) */
  text: string;
  /**
   * The intent associated with the example.
   * Tells you what the right intent would be for the question (`text`).
   * It is not defined if the example is taken from feedback corrections.
   */
  intent?: string | null;
  /**
   * For positive corrections only. The label of the example. E.g. "0" or "1".
   * You find the intent by matching this label with the `posLabel` in {@link K2ProtocolFeedbackPair},
   * but it's not really useful.
   */
  label?: string | null;
  /**
   * The description of the example/user query. May or may no tbe present.
   * Usually not present if the example is a correction.
   */
  description?: string | null;
}

/** Enriched protocol details of a K2 classification. */
export interface K2Protocol {
  /** The input text */
  text: string;
  /** The prompt details sent to the LLM */
  prompt: K2Prompt[];
  /** The response from the model (a short decision indicator) */
  response: string;
  /** The type of call made */
  callType: string;
  /** The time tracking details */
  timeTracking: K2TimeTracking;
  /** The a list of topics that were presented to the LLM as candidate options for detecting what the user wanted */
  presentedTopics: K2Intent[];
  /** The indexed intents (ELABORATION NEEDED) */
  indexIntents: K2Intent[];
  /**
   * Randomly picked reference variants and positive AI feedbacks to re-enforce the model, among other things.
   * It can also contain the "result" of a manual correction, in which case the object is slightly different.
   * It can also contain the question the user asked originally, and the question was mapped to the currently detected intent as the right answer.
   */
  examples: K2Example[];
  /**
   * An expert's corrections to what is the right detection and what is the wrong detection of intent.
   * A feedback pair from this list explicitly tells you that an expert matched a user query to the currently detected intent.
   */
  feedbackPairs: null | K2ProtocolFeedbackPair[];
  /**
   * Rated as bad matches.
   * These are included in the current intent to explicitly inform the user that "you've told the AI to not find this
   * intent if they ask X question". Can be null if none, or array of {@link K2ProtocolNegativeFeedback}s.
   */
  negFeedbacks: null | K2ProtocolNegativeFeedback[];
  /** The unique user ID o the conversation (usually `null` for the playground) */
  uniqueUserId: null | string;
  /** The channel ID it was asked in */
  channelId: string;
  /** The conversation ID (usually null in AI Playground) */
  conversationId: null | string;
  /**
   * Bot-wide or channel-specific bot context.
   * Gives the LLM extra information, e.g. "You are an agent for company X...".
   * Not often used, but is either a human's additional instruction to the LLM or `null`.
   */
  botContext: string | null;
}

const ProductService = {
  getIntents: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get('/bot/faq/' + botId);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param template Intent template name, e.g. 'BotForm'
   * @param intent Technical intent name
   */
  getIntentDetails: async function (
    botId: string,
    template: string,
    intent: string
  ): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/intent/${template}/${intent}/${botId}`
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getForms: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get('/schaltzentrale/forms/' + botId);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getAllForms: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        '/schaltzentrale/forms/get/' + botId
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * Where a form is currently referenced from.
   *
   * Same payload the delete endpoint answers 409 with, just without attempting the
   * deletion, so it is safe to call speculatively. The analysis is channel-agnostic:
   * the report covers every channel at once and splits scope per stage internally, so
   * there is nothing to re-fetch when the editor switches channel.
   *
   * @returns The report, or `null` if it could not be loaded (non-critical).
   */
  getFormUsages: async function (
    botId: string,
    intent: string
  ): Promise<FormUsageReport | null> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/forms/${botId}/${intent}/usages`
      );
      return response.data;
    } catch (error) {
      // Non-critical: the editor works fine without it, so fail quietly
      console.error('Could not load form usage report', error);
      return null;
    }
  },
  /**
   * Deletes a form. The API re-checks usages on the delete itself and answers 409 with
   * the usage report when the form is still referenced, so callers can show what is in
   * the way rather than a bare failure.
   */
  deleteForm: async function (
    botId: string,
    intent: string
  ): Promise<DeleteFormResult> {
    try {
      const response = await ApiService.customRequest({
        method: 'delete',
        url: `/schaltzentrale/forms/${botId}/${intent}`,
      });

      // The endpoint answers 200 with `{status: 'ok'|'error'}`, so normalise it to
      // the same shape the rejection path below returns.
      if (response.data?.status !== 'ok') {
        return { success: false, error: 'Delete failed', data: null };
      }

      return { success: true };
    } catch (error: any) {
      // 409 because the resource is still referenced. Data is a detailed analysis the UI can use.
      if (error.status === 409 || error.response?.status === 409) {
        return {
          success: false,
          error: 'form still in use',
          data: error.response?.data ?? null,
        };
      }

      console.error(error);
      return { success: false, error: 'Unknown error', data: null };
    }
  },
  updateFormDisplayName: async function (
    botId: string,
    intent: string,
    displayName: string
  ): Promise<any> {
    try {
      const response = await ApiService.put(
        '/schaltzentrale/forms/' + botId + '/' + intent,
        {
          displayName,
        }
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param box Content box name; falls back to the default box segment when falsy
   * @param channel Channel id; falls back to the default channel segment when falsy
   */
  getContent: async function (
    botId: string,
    template: string,
    intent: string,
    box?: string | null,
    channel?: string | null
  ): Promise<any> {
    try {
      let url = `/schaltzentrale/intent/content/${botId}/${intent}/${template}`;
      if (box) {
        url += `/box/${box}`;
      } else {
        url += `/box`;
      }

      if (channel) {
        url += `/channel/${channel}`;
      } else {
        url += `/channel`;
      }

      const response = await ApiService.get(url);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param box Unused by the request; kept for caller compatibility
   * @param content Full intent content document to save
   */
  saveContent: async function (
    botId: string,
    box: any,
    content: any
  ): Promise<any> {
    /* console.log('SAVE', botId, box, content); */

    try {
      const response = await ApiService.post(
        '/schaltzentrale/intent/' + botId,
        content
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param measurement Conversion-measurement setting for the intent
   */
  changeMeasurement: async function (
    botId: string,
    intent: string,
    measurement: any
  ): Promise<any> {
    const body = {
      intent,
      measurement,
    };

    try {
      const response = await ApiService.post(
        `/schaltzentrale/intent/measurement/` + botId,
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param type Content type segment of the endpoint, e.g. 'actions'
   * @param actions New content payload for that type
   */
  updateBotContent: async function (
    botId: string,
    intent: string,
    type: string,
    actions: any
  ): Promise<any> {
    try {
      const response = await ApiService.post(
        '/schaltzentrale/intent/' + intent + `/${type}/` + botId,
        actions
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param intent Intent document; the whitelisted fields are sent as the update
   * @returns `{}` on error, otherwise `{status, intent?, message?, tags?}`
   */
  updateIntent: async function (intent: any): Promise<any> {
    try {
      const body: any = {
        uniqueBotId: intent.botId,
        intent: intent.intent,
        displayName: intent.displayName,
        description: intent.description,
        zeroshotLabel: intent.zeroshotLabel,
        standardIntent: intent.standardIntent,
        translation: intent.translation,
      };

      if (intent.confidenceLimit) {
        body.confidenceLimit = intent.confidenceLimit;
      }

      if (intent.tags) {
        body.tags = intent.tags;
      }

      const response = await ApiService.post('/schaltzentrale/intent', body);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param intent Intent-like object providing `botId`, `intent` and `forward` */
  updateSuggestion: async function (intent: any): Promise<any> {
    try {
      const body = {
        uniqueBotId: intent.botId,
        intent: intent.intent,
        forward: intent.forward,
      };

      const response = await ApiService.post(
        '/schaltzentrale/intent/suggestion',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param fromBox Source content box to analyze
   * @param toBoxes Target boxes to compare against
   */
  analyze: async function (
    botId: string,
    fromBox: any,
    toBoxes: any
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        fromBox,
        toBoxes,
      };

      const response = await ApiService.post(
        '/schaltzentrale/intent/analyze',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param samples Example user queries for the new intent
   * @param suggested Whether the intent starts as a suggestion
   * @param forward Forward/redirect target of the suggestion
   */
  addIntent: async function (
    uniqueBotId: string,
    samples: string[],
    displayName: string,
    description: string,
    suggested: any,
    forward: any
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId,
        samples,
        displayName,
        description,
        suggested,
        forward,
      };

      const response = await ApiService.post(
        '/schaltzentrale/intent/new',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  addForm: async function (
    uniqueBotId: string,
    displayName: string,
    template: string = 'BotForm'
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId,
        displayName,
        template,
      };

      const response = await ApiService.post('/schaltzentrale/forms', body);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param name Technical name of the intent whose suggestion state is toggled */
  changeIntentSuggestion: async function (
    uniqueBotId: string,
    name: string
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId,
        name,
      };

      const response = await ApiService.post(
        '/schaltzentrale/intent/suggest',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getTrainingdata: async function (botId: string, name: string): Promise<any> {
    try {
      const response = await ApiService.get(
        '/schaltzentrale/trainingdata/' + botId + '/' + name
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param dateRange Two-element `[start, end]` moment-like range (needs `.format()`)
   */
  getTrainingdataPeriod: async function (
    botId: string,
    dateRange: any[]
  ): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/trainingdata/${botId}/${dateRange[0].format('YYYY-MM-DD')}/${dateRange[1].format('YYYY-MM-DD')}`
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param name Intent the training data belongs to
   * @param trainingdata Training samples to add
   */
  addTrainingdata: async function (
    botId: string,
    name: string,
    trainingdata: any
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        name,
        trainingdata,
      };
      const response = await ApiService.post(
        '/schaltzentrale/trainingdata',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param text The training sample text to delete */
  deleteTrainingdata: async function (
    botId: string,
    text: string
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        text,
      };
      const response = await ApiService.delete(
        '/schaltzentrale/trainingdata',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param variant Training-data variant document; needs `botId` and `_id` */
  updateTrainingdata: async function (variant: any): Promise<any> {
    try {
      const body = {
        uniqueBotId: variant.botId,
        id: variant._id,
        variant,
      };
      const response = await ApiService.post(
        '/schaltzentrale/trainingdata/update',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param variant Training-data variant document; needs `botId` and `_id` */
  updateQualityCheck: async function (variant: any): Promise<any> {
    try {
      const body = {
        uniqueBotId: variant.botId,
        id: variant._id,
      };
      const response = await ApiService.post(
        '/schaltzentrale/trainingdata/qualitycheck',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param noReferenceMatch Skip reference-variant matching during the check
   * @param noKeywordMatch Skip keyword matching during the check
   */
  nlpCheck: async function (
    botId: string,
    text: string,
    noReferenceMatch: boolean = false,
    noKeywordMatch: boolean = false,
    channelId: string | null = null
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        text,
        noReferenceMatch,
        noKeywordMatch,
        channelId
      };

      const response = await ApiService.post('/schaltzentrale/nlp/check', body);
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param feedback Feedback verdict for the detection (e.g. positive/negative)
   * @param intent Intent the feedback applies to
   */
  nlpFeedback: async function (
    botId: string,
    text: string,
    feedback: any,
    intent: string
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        text,
        feedback,
        intent,
      };

      const response = await ApiService.post(
        '/schaltzentrale/nlp/feedback',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getNlpFeedbacks: async function (
    botId: string,
    intent?: string
  ): Promise<{ success: boolean; data?: any[]; error?: any }> {
    try {
      // Leave intent empty to fetch all aiFeedbacks related to this bot
      const response = await ApiService.get(`/schaltzentrale/nlp/feedback/${botId}/${intent}`);
      return { success: true, data: response.data || [], };
    } catch (error) {
      return { success: false, error };
    }
  },
  /**
   * Delete/withdraw feedback by the feedback record ID
   * @param botId Bot the feedback belongs to
   * @param id Object ID to the feedback document
   */
  deleteFeedback: async function (
    botId: string,
    id: string
  ): Promise<{ success: boolean; [key: string]: any }> {
    try {
      const response = await ApiService.delete(
        `/schaltzentrale/nlp/feedback/${botId}/${id}`,
      );
      const data = response.data;
      return { success: data.success ?? false, ...data };
    } catch (error) {
      return { success: false, error };
    }
  },
  getDeployment: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        '/schaltzentrale/deployment/' + botId
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * Incrementally stream deployment details.
   * The stream is newline-delimited JSON; each line is routed to the given callbacks.
   */
  streamDeploymentDetails: async function (
    botId: string,
    options: StreamDeploymentOptions = {}
  ): Promise<any> {
    const { onSize, onPart, signal, onFinish } = options;

    let buffer = '';

    const processChunk = (chunk: any) => {
      if (typeof chunk !== 'string') return;
      buffer += chunk;

      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line) {
          try {
            const parsed = JSON.parse(line);
            if (parsed.type === 'totalParts') {
              onSize?.(parsed.data);
            } else if (parsed.type === 'details') {
              onPart?.(parsed.data);
            } else if (parsed.type === 'status') {
              if (parsed.status !== 'ok') {
                console.error('Deployment stream finished with non-ok status:', parsed);
              }
            }
          } catch (error) {
            console.log('Failed to parse deployment stream line:', line, error);
          }
        }
      }
    };

    try {
      await ApiService.stream(`/schaltzentrale/deployment-details/${botId}`,
          { onChunk: processChunk, signal, onFinish });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw error;
      }

      if (error.response) {
        const rawData = error.response.data;
        let payload: any = {};
        if (typeof rawData === 'string') {
          try {
            payload = JSON.parse(rawData);
          } catch (parseError) {
            payload = { message: rawData || 'Unable to load deployment details' };
          }
        } else if (rawData && typeof rawData === 'object') {
          payload = rawData;
        }

        const err: any = new Error(payload.message || 'Unable to load deployment details');
        err.status = payload.status ?? 'error';
        throw err;
      }

      // For network errors or other fetch errors without response, throw directly
      throw error;
    }
  },
  /**
   * Triggers a deployment for the bot.
   * @returns `{status, success}`; on request error `{status: "ok", success: false}`
   */
  deploy: async function (
    botId: string
  ): Promise<{ status: string; success: boolean }> {
    try {
      const body = {
        uniqueBotId: botId,
      };
      const response = await ApiService.post(
        '/schaltzentrale/deployment',
        body
      );
      return response.data;
    } catch (error) {
      return { status: "ok", success: false };
    }
  },
  /**
   * @param texts Texts to cluster
   * @param cluster Clustering options/target
   */
  nlpCluster: async function (
    botId: string,
    texts: string[],
    cluster: any
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        texts,
        cluster,
      };

      const response = await ApiService.post(
        '/schaltzentrale/nlp/cluster',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param lastChangeDate Timestamp to mark as the deployed change date */
  fakeDeploy: async function (
    botId: string,
    lastChangeDate: any
  ): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        lastChangeDate,
      };
      const response = await ApiService.post(
        '/schaltzentrale/fakeDeployment',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getNLPModels: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        '/schaltzentrale/nlp/model/' + botId
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  deleteNLPModel: async function (
    botId: string,
    modelid: string
  ): Promise<any> {
    try {
      const response = await ApiService.delete(
        '/schaltzentrale/nlp/model/' + botId + '/' + modelid
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param live Whether to update the live model pointer
   * @param staging Whether to update the staging model pointer
   * @param modelType NLP model family, e.g. 'k1'
   */
  updateNLPModel: async function (
    botId: string,
    modelId: string,
    live: boolean,
    staging: boolean,
    modelType: string = 'k1'
  ): Promise<any> {
    try {
      const body = {
        modelId,
        updateStaging: staging,
        updateLive: live,
        modelType,
        stage: 'prod', // change to dev
      };
      const response = await ApiService.put(
        '/schaltzentrale/bot/' + botId + '/model',
        body
      );
      return response;
    } catch (error) {
      return {};
    }
  },
  /** Downloads the model to dev, cluster and prod stages in sequence. */
  uploadNLPModel: async function (
    botId: string,
    modelId: string
  ): Promise<any> {
    try {
      await ApiService.post(
        `/schaltzentrale/nlp/model/download/${botId}/dev/${modelId}`
      );
      await ApiService.post(
        `/schaltzentrale/nlp/model/download/${botId}/cluster/${modelId}`
      );
      const response = await ApiService.post(
        `/schaltzentrale/nlp/model/download/${botId}/prod/${modelId}`
      );
      return response;
    } catch (error) {
      return {};
    }
  },
  /** @param licence Licence document to attach to the bot */
  addLicence: async function (botId: string, licence: any): Promise<any> {
    try {
      const body = {
        licence,
      };
      const response = await ApiService.put(
        '/schaltzentrale/bot/' + botId + '/licence',
        body
      );
      return response;
    } catch (error) {
      return {};
    }
  },
  /** @param licenceStart Start date identifying which licence to delete */
  deleteLicence: async function (
    botId: string,
    licenceStart: any
  ): Promise<any> {
    try {
      const body = {
        start: licenceStart,
      };
      const response = await ApiService.delete(
        '/schaltzentrale/bot/' + botId + '/licence',
        body
      );
      return response;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param licenceStart Start date identifying which licence to change
   * @param cancel Cancellation state to set
   */
  cancelLicence: async function (
    botId: string,
    licenceStart: any,
    cancel: any
  ): Promise<any> {
    try {
      const body = {
        start: licenceStart,
        cancel,
      };
      const response = await ApiService.post(
        '/schaltzentrale/bot/' + botId + '/licence/cancel',
        body
      );
      return response;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param licenceStart Start date identifying which licence to change
   * @param confirm Confirmation state to set
   */
  confirmLicence: async function (
    botId: string,
    licenceStart: any,
    confirm: any
  ): Promise<any> {
    try {
      const body = {
        start: licenceStart,
        confirm,
      };
      const response = await ApiService.post(
        '/schaltzentrale/bot/' + botId + '/licence/confirm',
        body
      );
      return response;
    } catch (error) {
      return {};
    }
  },
  getLicences: async function (): Promise<any> {
    try {
      const response = await ApiService.get('/schaltzentrale/licences');
      return response.data;
    } catch (error) {
      return {};
    }
  },
  validateDefaultRules: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/validator/default/${botId}`
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param version Validator ruleset version to apply fixes for */
  fixValidateDefaultRules: async function (
    botId: string,
    version: string = '1.0.0'
  ): Promise<any> {
    try {
      const body = {
        version,
      };
      const response = await ApiService.post(
        `/schaltzentrale/validator/default/${botId}`,
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param from Range start (moment); defaults to 30 days ago
   * @param to Range end (moment); defaults to the start of today
   */
  getWisdomStats: async function (
    from: any = moment().add(-30, 'days'),
    to: any = moment().startOf('days')
  ): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/stats/wisdom/all/${from.format(
          'YYYY-MM-DD'
        )}/${to.format('YYYY-MM-DD')}?sort=aifeedback`
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  getBotsAccuracy: async function (botIds: string[]): Promise<any> {
    try {
      const response = await ApiService.post(
        '/schaltzentrale/stats/accuracy/all',
        { bots: botIds }
      );
      return response.data;
    } catch (error) {
      return { result: [] };
    }
  },
  /**
   * Deletes an intent (topic or AI agent). The API re-checks usages on the delete itself
   * and answers 409 with the usage report when the intent is still referenced, so the
   * caller can show what is in the way rather than a bare failure.
   */
  deleteIntent: async function (botId: string, intent: string): Promise<any> {
    try {
      const response = await ApiService.delete(
        `/schaltzentrale/intent/${botId}/${intent}`
      );
      return response.data;
    } catch (error: any) {
      // Still referenced somewhere. Data is the same analysis the usage check returns.
      if ((error.status || error.response?.status) === 409) {
        return { status: 'error', usageReport: error.response.data };
      }
      return {};
    }
  },
  /**
   * @param locale CMS locale, e.g. 'de'
   * @param stage CMS content stage, e.g. 'demo'
   */
  getKnowledgeBaseItems: async function (
    locale: string = 'de',
    stage: string = 'demo'
  ): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/cms/knowledge/${locale}/${stage}`
      );
      return response.data.items;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param locale CMS locale, e.g. 'de'
   * @param stage CMS content stage, e.g. 'demo'
   */
  getExpertItems: async function (
    locale: string = 'de',
    stage: string = 'demo'
  ): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/cms/expert/${locale}/${stage}`
      );
      return response.data.items;
    } catch (error) {
      return {};
    }
  },
  getContacts: async function (): Promise<any> {
    try {
      const response = await ApiService.get('/schaltzentrale/cms/contacts');
      return response.data.items;
    } catch (error) {
      return {};
    }
  },
  getContact: async function (id: string | null | undefined): Promise<any> {
    if (!id) return null;

    try {
      const response = await ApiService.get(
        `/schaltzentrale/cms/contact/${id}`
      );
      return response.data.item;
    } catch (error) {
      return null;
    }
  },
  // DEPRECATED use addIntentFromSource instead
  addSmalltalk: async function (
    botId: string,
    smalltalk: string
  ): Promise<any> {
    if (!smalltalk) return null;

    try {
      const response = await ApiService.post(
        `/schaltzentrale/intent/add/smalltalk/${botId}`,
        { intentName: smalltalk }
      );
      return response.data;
    } catch (error) {
      return null;
    }
  },
  /**
   * Copies an intent from a source bot into this bot.
   * @param sourceBot Bot to copy the intent from
   */
  addIntentFromSource: async function (
    botId: string,
    intent: string,
    sourceBot: string
  ): Promise<any> {
    if (!intent || !sourceBot) return null;

    try {
      const response = await ApiService.post(
        `/schaltzentrale/intent/add/smalltalk/${botId}`,
        { intentName: intent, sourceBot },
        120000
      );
      return response.data;
    } catch (error) {
      return null;
    }
  },
  addNewIntent: async function ({
    botId,
    samples,
    displayName,
    scenario,
    description,
    template,
  }: {
    /** Bot the intent is created for */
    botId: string;
    /** Example user queries */
    samples: string[];
    /** Display name of the new intent */
    displayName: string;
    /** Scenario/use-case description used by the review flow */
    scenario?: string;
    /** Description of the new intent */
    description?: string;
    /** Intent template name */
    template?: string;
  }): Promise<any> {
    try {
      const body = {
        uniqueBotId: botId,
        samples,
        displayName,
        scenario,
        description,
        template,
      };

      const response = await ApiService.post(
        '/schaltzentrale/intent/new/review',
        body
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },
  /** @param nlpTemplate Template bot reference (`displayName` + `botId`) */
  setNlpTemplate: async function (
    botId: string,
    nlpTemplate: { displayName: string; botId: string }
  ): Promise<any> {
    try {
      const body = {
        displayName: nlpTemplate.displayName,
        templateBotId: nlpTemplate.botId,
        uniqueBotId: botId,
      };

      const response = await ApiService.post(
        '/schaltzentrale/bot/nlpTemplate',
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  generateTrainingdata: async function ({
    botId,
    intentName,
    iterations = 3,
    sampleSize = 10,
    alternateDescription,
    negativeExamples,
  }: {
    /** Bot the training data is generated for */
    botId: string;
    /** Intent to generate variants for */
    intentName: string;
    /** How many generation rounds to run */
    iterations?: number;
    /** How many samples per round */
    sampleSize?: number;
    /** Alternative intent description to steer generation; `null` uses the stored one */
    alternateDescription?: string | null;
    /** Example queries that should NOT match the intent */
    negativeExamples?: string[] | null;
  }): Promise<any> {
    try {
      const body = {
        intent: intentName,
        config: {
          translate: false,
          translateBack: false,
          iterations,
          sampleSize,
          numExampleTopics: 1,
          alternateDescription: alternateDescription || null,
          negativeExamples: negativeExamples?.length ? negativeExamples : null,
        },
      };

      const response = await ApiService.post(
        `/schaltzentrale/vgeneration/${botId}`,
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param body Copy request payload (source bot/intent etc.) */
  copyIntent: async function (targetBotId: string, body: any): Promise<any> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/intent/copy/${targetBotId}`,
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param params.sourceBotId [SOURCE] The ID of the bot from which the channel is copied
   * @param params.targetBotId [TARGET] The ID of the bot to which the channel is copied
   * @param params.intentName [SOURCE] The name of the intent to copy
   * @param params.targetIntent [TARGET] The name of the intent to which the channel is copied
   * @param params.channelFrom [SOURCE] The ID of the channel from which the intent is copied
   * @param params.channelTo [TARGET] The ID of the channel to which the intent is copied
   * @param opts.timeout Max time we will wait for the HTTP request response. Defaults to 1 minute
   * @returns `{status: 'ok', intent}` or `{status: 'error', error}`
   */
  copyChannel: async function (
    {
      sourceBotId,
      targetBotId,
      intentName,
      targetIntent,
      channelFrom,
      channelTo,
    }: {
      sourceBotId: string;
      targetBotId: string;
      intentName: string;
      targetIntent: string;
      channelFrom: string | null;
      channelTo: string | null;
    },
    opts: { timeout?: number } = {},
  ): Promise<{ status: 'ok'; intent: any } | { status: 'error'; error: string }> {
    const body = {
      intentName,
      sourceBot: sourceBotId, // LiveBotId
      targetIntent: targetIntent,
      sourceChannelId: channelFrom,
      targetChannelId: channelTo,
      changelogRecord: true,
    };
    try {
      const response = await ApiService.post(
        `/schaltzentrale/intent/copy/channel/${targetBotId}`,
        body,
        opts.timeout || 60_000,
      );
      return { status: 'ok', intent: response.data };
    } catch (error: any) {
      let errorMessage = error?.message;
      const details = error?.response?.data?.message || error?.response?.data;
      if (details) errorMessage += `. Details: ${typeof details === 'string' ? details : JSON.stringify(details)}`;
      return { status: 'error', error: errorMessage };
    }
  },
  setInReview: async function (
    botId: string,
    intent: string,
    inReview: boolean = false
  ): Promise<any> {
    try {
      const body = {
        name: intent,
        inReview,
        uniqueBotId: botId,
      };
      const response = await ApiService.post(
        `/schaltzentrale/intent/inreview`,
        body
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /** @param type Training type segment, e.g. 'reference' */
  training: async function (
    botId: string,
    type: string = 'reference'
  ): Promise<any> {
    try {
      if (!botId) return false;

      const response = await ApiService.post(
        `/schaltzentrale/nlp/model/${botId}/training/${type}`
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },
  /** @returns The translated text; `undefined` on error (pre-existing behavior) */
  translate: async function (
    text: string,
    language: string,
    botId: string
  ): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/translate/` + botId, {
        text,
        language,
      })
      return response.data.translation
    } catch (error) {
      // empty
    }
  },
  /**
   * Perform a K2 index check using a text
   * @param params.botId Bot to classify against
   * @param params.channelId Channel context of the check
   * @param params.text Your chat input
   * @param params.conversationId Conversation the check belongs to
   * @param params.withLastIntent Override for the last detected intent
   * @param params.withProtocol Enrich the response with more protocol details
   * @param params.withQuickReplies Specify an override for quick-replies available
   * @param params.withPreviousConversation Specify an override/manual previous conversation
   * @param params.withNarrowingTurnsUsed Refinement turns already used in this conversation (intelligent follow-ups)
   * @returns `{}` on error, otherwise `{status: 'ok'|'error', result: K2CheckResult}`
   */
  k2check: async function ({
    botId,
    channelId,
    text,
    withProtocol,
    conversationId,
    withLastIntent,
    withQuickReplies,
    withPreviousConversation,
    withNarrowingTurnsUsed,
  }: {
    botId: string;
    channelId: string;
    text: string;
    withProtocol?: boolean;
    conversationId?: string | null;
    withLastIntent?: string | null;
    withQuickReplies?: null | { label: string; intent: { name: string } }[];
    withPreviousConversation?: null | { role: 'User' | 'Assistant'; text: string }[];
    withNarrowingTurnsUsed?: number;
  }): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/k2/classify/`, {
        text,
        conversationId,
        channelId,
        withLastIntent,
        withQuickReplies,
        withPreviousConversation,
        withNarrowingTurnsUsed,
        uniqueBotId: botId,
        staging: true,
        returnProtocol: !!withProtocol,
      });
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param ragProtocolId Protocol entry the feedback refers to
   * @param feedbackText Free-text feedback from the user
   * @param text The original user question
   * @param responseText The AI answer being rated
   */
  addRagFeedback: async function (
    ragProtocolId: string,
    intent: string,
    botId: string,
    feedbackText: string | null = null,
    text: string | null = null,
    responseText: string | null = null
  ): Promise<any> {
    try {
      const body = {
        ragProtocolId,
        intent,
        botId,
        feedbackText,
        text,
        response: responseText,
      };

      const response = await ApiService.post('/schaltzentrale/rag-feedback/rag-feedback', body);
      const data = response.data;
      if (data.success) {
        return data.data;
      } else {
        console.error('Failed to add RAG feedback:', data);
        return {};
      }
    } catch (error) {
      console.error('Error adding RAG feedback:', error);
      return {};
    }
  },

  removeRagFeedback: async function (
    ragFeedbackId: string,
    botId: string
  ): Promise<any> {
    try {
      const response = await ApiService.delete(`/schaltzentrale/rag-feedback/rag-feedback/${ragFeedbackId}`, {
        data: {
          botId,
        },
      });
      return response.data;
    } catch (error) {
      return {};
    }
  }
};

export default ProductService;

export { ProductService };
